import { Injectable } from '@angular/core';
import {HttpClient, HttpEvent, HttpParams} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {Conversation, ConversationStatus} from '../contracts/conversation.interface';
import {Message} from "../contracts/message.interface";
import {TableFilter} from '../../shared/contracts/table-column.interface';

@Injectable({
  providedIn: 'root',
})
export class ConversationService {
  private readonly API_URL = 'conversations/';

  constructor(private http: HttpClient) {}

  getAll(
    searchMessage?: string,
  ): Observable<{ data: Conversation[]; total: number }> {
    let params = new HttpParams();

    if (searchMessage) {
      params = params.set('message', searchMessage);
    }

    return this.http.get<Conversation[]>(
      `${this.API_URL}active`,
      { params }
    ).pipe(map(data => ({ data, total: data.length })));
  }

  getAllToManage(
    status?: ConversationStatus | null,
    assignedUserId?: number,
    dateFrom?: string,
    dateTo?: string,
    page: number = 1,
    size: number = 10,
    filters: TableFilter[] = []
  ): Observable<{ data: Conversation[], total: number }> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (status !== null && status !== undefined) {
      params = params.set('status', status);
    }

    if (assignedUserId !== undefined) {
      params = params.set('assignedUserId', assignedUserId);
    }

    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    for (const f of filters) {
      params = params.append('filter_field', f.field);
      params = params.append('filter_value', f.value);
      params = params.append('filter_match', f.matchMode);
    }

    return this.http.get<{ data: Conversation[], total: number }>(
      `${this.API_URL}manage`,
      { params }
    );
  }

  getById(id: number): Observable<Conversation> {
    return this.http.get<Conversation>(`${this.API_URL}${id}`);
  }

  create(conversation: Conversation): Observable<Conversation> {
    return this.http.post<Conversation>(this.API_URL, conversation);
  }

  createManual(payload: {phone_number: string; status: ConversationStatus; priority: string}): Observable<Conversation> {
    return this.http.post<Conversation>(`${this.API_URL}manual`, payload);
  }

  updateNotes(id: number, notes: string | null): Observable<void> {
    return this.http.patch<void>(`${this.API_URL}${id}/notes`, {notes});
  }

  updateMessageNote(messageId: number, note: string | null): Observable<Message> {
    return this.http.patch<Message>(`messages/${messageId}`, { note });
  }

  getRecents(messageSearch?: string): Observable<Conversation[]> {
    let params = new HttpParams();
    if (messageSearch) params = params.set('message', messageSearch);
    return this.http.get<Conversation[]>(`${this.API_URL}recent`, { params });
  }

  update(id: number, conversation: Conversation): Observable<Conversation> {
    return this.http.put<Conversation>(`${this.API_URL}${id}`, conversation);
  }

  delete(id: number): Observable<Conversation> {
    return this.http.delete<Conversation>(`${this.API_URL}${id}`);
  }

  getMessages(id: number, page: number, limit: number) {
    return this.http.get<Message[]>(
      `${this.API_URL}${id}/messages`,
      {
        params: {
          page: page,
          limit: limit
        }
      }
    );
  }

  getMessagesContext(id: number, messageId: number) {
    return this.http.get<Message[]>(
      `${this.API_URL}${id}/messages/${messageId}/context`,
    );
  }

  getQuotes(id: number, skip: number, limit: number) {
    return this.http.get<Message[]>(`${this.API_URL}${id}/quotes`, {
      params: { skip: skip.toString(), limit: limit.toString() }
    });
  }

  getMedia(id: number, skip: number, limit: number) {
    return this.http.get<Message[]>(`${this.API_URL}${id}/media`, {
      params: { skip: skip.toString(), limit: limit.toString() }
    });
  }


  // getAllQuotes(id: number, skip: number, limit: number) {
  //   return this.http.get<Message[]>(`${this.API_URL}${id}/quotes-all`, {
  //     params: { skip: skip.toString(), limit: limit.toString() }
  //   });
  // }

  assignConversation(id: number, userToAssignId: number): Observable<void> {
    return this.http.post<void>(`${this.API_URL}${id}/assign/${userToAssignId}`, {});
  }

  revokeAssignment(id: number): Observable<void> {
    return this.http.post<void>(`${this.API_URL}${id}/revoke`, {});
  }

  respondToAssignment(id: number, choice: boolean){
    return this.http.post<void>(`${this.API_URL}${id}/respond-to-assignment`, {assignmentResponse: choice});
  }

  closeConversation(id: number){
    return this.http.post<void>(`${this.API_URL}${id}/close`, {});
  }

  readConversation(id: number){
    return this.http.post<void>(`${this.API_URL}${id}/read`, {});
  }

  reopenConversation(id: number){
    return this.http.post<void>(`${this.API_URL}${id}/reopen`, {});
  }

  uploadFile(id: number, file: File, isQuote: boolean, replyMessageId: any = null): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_quote', String(isQuote));

    if (replyMessageId !== null) {
      formData.append('reply_message_id', replyMessageId.toString());
    }

    return this.http.post<any>(`${this.API_URL}${id}/upload`, formData);
  }

  sendMetaTemplate(id: number): Observable<HttpEvent<any>> {
    return this.http.post<any>(`${this.API_URL}${id}/send-reopen-template`, {});
  }

  updatePriority(id: number, conversation: Conversation): Observable<Conversation> {
    return this.http.put<Conversation>(`${this.API_URL}${id}/priority`, conversation);
  }
}
